<script setup>
import queryString from "query-string";
import { watchDebounced } from "@vueuse/core";

definePageMeta({
  auth: false,
});

const pageNumber = ref(1);
const pageSize = ref(10);

const search = ref("");
const debounceSearch = ref(search.value);

const showSearch = ref(false);
const universityIds = ref([]);

watchDebounced(
  search,
  (newValue) => {
    pageNumber.value = 1;
    debounceSearch.value = newValue;
  },
  { debounce: 500, maxWait: 6000 },
);

const queryStr = computed(() => {
  const qStr = queryString.stringify(
    {
      page: pageNumber.value,
      pageSize: pageSize.value,
      name: debounceSearch.value || null,
      university_ids: universityIds.value,
    },
    { arrayFormat: "index" },
  );

  return qStr;
});

const { data: scholarships, status } = usePublicFetch(
  () => `scholarships?published=${true}&${queryStr.value}`,
  {
    method: "GET",
    key: "scholarships",
  },
);

const handleOnChange = (ids) => {
  universityIds.value = ids;
};

const loadData = (event) => {
  const { page } = event;
  pageNumber.value = page + 1;
};

// --- SEO ---
useHead({
  link: [{ rel: "canonical", href: "https://unilinks.co/scholarships" }],
});

useSeoMeta({
  title: "Thailand Scholarships for International Students | UniLinks",
  ogTitle: "Thailand Scholarships for International Students | UniLinks",
  description:
    "Find scholarships for studying in Thailand including MBA international student scholarships, Thailand scholarship programs, and double degree options.",
  ogDescription:
    "Find scholarships for studying in Thailand including MBA international student scholarships, Thailand scholarship programs, and double degree options.",
  ogImage: META_IMAGE_URL,
  ogUrl: "https://unilinks.co/scholarships",
  twitterCard: "summary_large_image",
  twitterTitle: "Thailand Scholarships for International Students | UniLinks",
  twitterDescription:
    "Find scholarships for studying in Thailand including MBA international student scholarships, Thailand scholarship programs, and double degree options.",
  twitterImage: META_IMAGE_URL,
});
</script>

<template>
  <NuxtLayout name="primary">
    <!-- Desktop View -->
    <div class="hidden lg:block bg-gray-50 px-4">
      <div class="mx-auto w-full max-w-7xl px-2 lg:px-0 pb-12">
        <div class="py-5 pt-12 flex items-center justify-between">
          <h1 class="text-3xl font-bold text-primary-500">
            {{ scholarships?.total }} scholarships in Thai Universities
          </h1>
          <div class="flex flex-row items-center gap-x-4">
            <UniversityMultiSelector
              :value="universityIds"
              @onChange="handleOnChange"
            />
            <InputText
              v-model="search"
              placeholder="Search by scholarship"
              class="w-64"
            />
          </div>
        </div>
        <div class="mt-8">
          <div class="grid grid-cols-2 gap-5">
            <div
              v-if="status === 'pending'"
              v-for="index in 8"
              class="col-1 flex items-center gap-8 py-2 px-8 rounded-lg border border-slate-200"
            >
              <Skeleton shape="circle" size="5rem"></Skeleton>
              <div class="flex-1">
                <Skeleton
                  width="40%"
                  height="1rem"
                  class="mb-4"
                  borderRadius="16px"
                ></Skeleton>
                <div class="flex gap-4">
                  <Skeleton
                    width="40%"
                    height="1.5rem"
                    class="mb-2"
                    borderRadius="16px"
                  ></Skeleton>
                  <Skeleton
                    width="40%"
                    height="1.5rem"
                    class="mb-2"
                    borderRadius="16px"
                  ></Skeleton>
                </div>
              </div>
            </div>
            <div
              v-else
              class="col-1"
              v-for="scholarship in scholarships?.data"
              :key="scholarship.id"
            >
              <ScholarshipCard :scholarship="scholarship" />
            </div>
          </div>

          <div class="text-center w-full">
            <Paginator
              :rows="pageSize"
              :totalRecords="scholarships?.total"
              @page="loadData"
              class="mt-10"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile View -->
    <div class="block lg:hidden bg-gray-50 mx-4">
      <div class="mx-auto w-full max-w-[1260px] xl:px-0">
        <div class="my-5 flex items-center justify-between">
          <h1 class="text-xl font-bold text-primary-500">
            {{ scholarships?.total }} scholarships
          </h1>
          <Button
            v-if="!showSearch"
            icon="pi pi-search"
            text
            rounded
            aria-label="Filter"
            @click="showSearch = true"
          />
          <Button
            v-else
            icon="pi pi-times"
            text
            rounded
            aria-label="Filter"
            @click="showSearch = false"
          />
        </div>

        <InputText
          v-if="showSearch"
          v-model="search"
          placeholder="Search by scholarship"
          class="w-full my-4"
        />

        <div class="grid grid-cols-1 gap-4 mb-6">
          <div
            v-if="status === 'pending'"
            v-for="index in 4"
            class="col-1 flex items-center gap-8 py-2 px-8 rounded-lg border border-slate-200"
          >
            <Skeleton shape="circle" size="5rem"></Skeleton>
            <div class="flex-1">
              <Skeleton
                width="40%"
                height="1rem"
                class="mb-4"
                borderRadius="16px"
              ></Skeleton>
              <div class="flex gap-4">
                <Skeleton
                  width="40%"
                  height="1.5rem"
                  class="mb-2"
                  borderRadius="16px"
                ></Skeleton>
                <Skeleton
                  width="40%"
                  height="1.5rem"
                  class="mb-2"
                  borderRadius="16px"
                ></Skeleton>
              </div>
            </div>
          </div>
          <div
            v-else
            v-for="scholarship in scholarships?.data"
            :key="scholarship.id"
          >
            <ScholarshipCard :scholarship="scholarship" />
          </div>
        </div>

        <Paginator
          v-if="status !== 'pending'"
          class="w-full"
          template="PrevPageLink CurrentPageReport NextPageLink"
          :rows="pageSize"
          :totalRecords="scholarships?.total"
          @page="loadData"
          currentPageReportTemplate="Page {currentPage} of {totalPages}"
          :pt="{
            root: 'px-4 w-full pb-4',
            content: 'flex justify-between items-center',
            prev: 'px-2.5 py-1.5 rounded-md border border-gray-300',
            next: 'px-2.5 py-1.5 rounded-md border border-gray-300',
          }"
        >
          <template #nexticon>
            <i class="pi pi-arrow-right" />
          </template>
          <template #previcon>
            <i class="pi pi-arrow-left" />
          </template>
        </Paginator>
      </div>
    </div>
  </NuxtLayout>

  <Footer />
</template>
